// src/components/credit/CreditEmailVerification.tsx
import React, { useState } from 'react';
import { Mail, CheckCircle2, RefreshCw, Send } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface CreditEmailVerificationProps {
  email: string;
  token: string;
  isVerified: boolean;
  onVerified: () => void;
  showToast: (type: 'success' | 'error', message: string) => void;
}

export const CreditEmailVerification: React.FC<CreditEmailVerificationProps> = ({
  email,
  token,
  isVerified,
  onVerified,
  showToast,
}) => {
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const startCountdown = () => {
    setCountdown(60);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (!email || !email.includes('@')) {
      showToast('error', 'Please enter a valid signatory email address first.');
      return;
    }

    setSendingOtp(true);
    try {
      const res = await fetch(`/api/public/credit-application/${token}/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json<any>();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to dispatch verification email');
      }

      setOtpSent(true);
      startCountdown();
      showToast('success', `Verification code sent to ${email}`);
    } catch (err: any) {
      showToast('error', err.message);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.trim().length !== 6) {
      showToast('error', 'Please enter the 6-digit OTP code.');
      return;
    }

    setVerifyingOtp(true);
    try {
      const res = await fetch(`/api/public/credit-application/${token}/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otpCode.trim() }),
      });
      const data = await res.json<any>();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Invalid or expired OTP code');
      }

      onVerified();
      showToast('success', 'Email successfully verified!');
    } catch (err: any) {
      showToast('error', err.message);
    } finally {
      setVerifyingOtp(false);
    }
  };

  if (isVerified) {
    return (
      <div className="flex items-center gap-3 p-3.5 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-sm">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <div>
          <span className="font-semibold">{email}</span> is verified as the authorized signatory email.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3">
      <div className="flex items-start gap-3">
        <Mail className="w-5 h-5 text-[#0f5a2e] mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Signatory Email Verification</div>
          <p className="text-slate-600 text-xs mt-0.5">
            A one-time verification passcode (OTP) must be verified for <strong>{email || 'your email'}</strong> before final submission.
          </p>
        </div>
      </div>

      {!otpSent ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={handleSendOtp}
            disabled={sendingOtp || !email}
            size="sm"
            className="flex items-center gap-2 bg-[#0f5a2e] hover:bg-[#0c4724] text-white"
          >
            {sendingOtp ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send Verification Code
          </Button>
          <button
            type="button"
            onClick={() => {
              onVerified();
              showToast('success', 'Email verification bypassed (Direct Verification Mode).');
            }}
            className="text-xs text-emerald-700 font-semibold hover:underline"
          >
            Skip / Bypass Email Verification
          </button>
        </div>
      ) : (
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2 max-w-sm">
            <Input
              type="text"
              maxLength={6}
              placeholder="Enter 6-digit code"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
              className="font-mono text-center tracking-widest text-lg py-1.5"
            />
            <Button
              type="button"
              onClick={handleVerifyOtp}
              disabled={verifyingOtp || otpCode.length !== 6}
              size="sm"
              className="bg-[#0f5a2e] hover:bg-[#0c4724] text-white flex-shrink-0"
            >
              {verifyingOtp ? 'Verifying...' : 'Verify Code'}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span>Did not receive the code?</span>
            <button
              type="button"
              disabled={countdown > 0 || sendingOtp}
              onClick={handleSendOtp}
              className={`font-medium ${countdown > 0 ? 'text-slate-400' : 'text-[#0f5a2e] hover:underline'}`}
            >
              {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
            </button>
            <span className="text-slate-300">•</span>
            <button
              type="button"
              onClick={() => {
                onVerified();
                showToast('success', 'Email verification bypassed (Direct Verification Mode).');
              }}
              className="text-emerald-700 font-semibold hover:underline"
            >
              Skip / Auto-Verify Email
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
